import { textColorFields } from '../_shared/textColor.js';
/**
 * order-confirmation — the thank-you page's own section (22/09/2026).
 *
 * Until this section existed, the last screen a buyer ever sees was painted entirely from code: the
 * seller could not change one word, one colour or one reassurance line of it, and it was invisible in the
 * builder — so the founder, looking for it, found nothing and concluded the page was simply ugly and
 * unowned. The markup and the palette below are the SAME validated design ("Page de remerciement COD");
 * what changed is that every decision it used to make silently is now a field.
 *
 * **Every text field starts EMPTY, not with a sentence.** A `defaultKey` would be the usual way to seed
 * copy, but it freezes that copy into the seller's document in ONE language at hydration time — and this
 * page's sentences are already translated into every storefront language under `confirmation.*`. An empty
 * field therefore means "keep saying what the shop already says, in the shop's own language", and only a
 * seller who types something takes that over. This is the same contract the COD button's label uses, and
 * it is the reason a shop that changes language keeps a thank-you page that speaks it.
 *
 * **Colours start empty too**, falling back to the page's success palette (and its dark-mode twin). A
 * seller who picks one gets it in both themes, which is the honest reading of "I chose this colour".
 *
 * The order itself is NEVER a setting. It arrives on the route (`context.page.routeType === 'order'`), so
 * nothing a seller can type changes what the buyer owes. In the builder there is no order, and the
 * component paints a sample one.
 */
export const orderConfirmationSchema = {
    type: 'order-confirmation',
    name: 'Order confirmation',
    version: 1,
    templates: ['thank-you'],
    fields: [
        // ── Hero ──────────────────────────────────────────────────────────────────
        {
            id: 'showCheck',
            type: 'checkbox',
            label: 'Show the success mark',
            group: 'Header',
            info: 'The green tick that draws itself above the title.',
            default: true,
        },
        {
            id: 'title',
            type: 'text',
            label: 'Title (buyer known)',
            group: 'Header',
            info: 'Use {name} for the buyer’s first name. Leave empty to keep your shop’s own wording, in your shop’s language.',
            default: '',
        },
        {
            id: 'titleAnonymous',
            type: 'text',
            label: 'Title (no name on the order)',
            group: 'Header',
            info: 'Shown when the order carries no name. Leave empty to keep your shop’s own wording.',
            default: '',
        },
        {
            id: 'subtitleBefore',
            type: 'text',
            label: 'Sentence before the order number',
            group: 'Header',
            info: 'The order number is printed between this and the next field. Leave both empty to keep your shop’s own wording.',
            default: '',
        },
        {
            id: 'subtitleAfter',
            type: 'text',
            label: 'Sentence after the order number',
            group: 'Header',
            default: '',
        },
        {
            id: 'showCodPill',
            type: 'checkbox',
            label: 'Show the “pay on delivery” pill',
            group: 'Header',
            info: 'Only ever shown on an order that is not already paid — a card order never sees it, whatever this says.',
            default: true,
        },
        {
            id: 'codPillText',
            type: 'text',
            label: 'Pill text',
            group: 'Header',
            default: '',
            visibleWhen: { field: 'showCodPill', equals: true },
        },
        // ── Order summary ─────────────────────────────────────────────────────────
        {
            id: 'showSummary',
            type: 'checkbox',
            label: 'Show the order summary',
            group: 'Summary',
            info: 'The card listing what was bought, the subtotal, the shipping and the amount due.',
            default: true,
        },
        {
            id: 'summaryTitle',
            type: 'text',
            label: 'Summary heading',
            group: 'Summary',
            default: '',
            visibleWhen: { field: 'showSummary', equals: true },
        },
        {
            id: 'showThumbnails',
            type: 'checkbox',
            label: 'Show product photos',
            group: 'Summary',
            default: true,
            visibleWhen: { field: 'showSummary', equals: true },
        },
        {
            id: 'showSavings',
            type: 'checkbox',
            label: 'Show what the buyer saved',
            group: 'Summary',
            info: 'The “you save” line, printed only when the order actually carries a discount.',
            default: true,
            visibleWhen: { field: 'showSummary', equals: true },
        },
        {
            id: 'showShipping',
            type: 'checkbox',
            label: 'Show the shipping line',
            group: 'Summary',
            default: true,
            visibleWhen: { field: 'showSummary', equals: true },
        },
        // ── Delivery ──────────────────────────────────────────────────────────────
        {
            id: 'showDelivery',
            type: 'checkbox',
            label: 'Show the delivery card',
            group: 'Shipping',
            info: 'The address the order is going to. Hidden by itself when the order carries no address.',
            default: true,
        },
        {
            id: 'deliveryTitle',
            type: 'text',
            label: 'Delivery heading',
            group: 'Shipping',
            default: '',
            visibleWhen: { field: 'showDelivery', equals: true },
        },
        {
            id: 'showCallNote',
            type: 'checkbox',
            label: 'Show the “we’ll call you” note',
            group: 'Shipping',
            info: 'Only on an order paid on delivery, and only when a phone number was given.',
            default: true,
            visibleWhen: { field: 'showDelivery', equals: true },
        },
        {
            id: 'callNoteBefore',
            type: 'text',
            label: 'Call note — before the number',
            group: 'Shipping',
            info: 'The buyer’s phone number is printed between this and the next field.',
            default: '',
            visibleWhen: { field: 'showCallNote', equals: true },
        },
        {
            id: 'callNoteAfter',
            type: 'text',
            label: 'Call note — after the number',
            group: 'Shipping',
            default: '',
            visibleWhen: { field: 'showCallNote', equals: true },
        },
        // ── Reassurance ───────────────────────────────────────────────────────────
        {
            id: 'showTrust',
            type: 'checkbox',
            label: 'Show the reassurance lines',
            group: 'Reassurance',
            info: 'Four short promises about what happens next. Empty a line to drop it.',
            default: true,
        },
        {
            id: 'trust1',
            type: 'text',
            label: 'Line 1 — delivery',
            group: 'Reassurance',
            default: '',
            visibleWhen: { field: 'showTrust', equals: true },
        },
        {
            id: 'trust2',
            type: 'text',
            label: 'Line 2 — payment',
            group: 'Reassurance',
            default: '',
            visibleWhen: { field: 'showTrust', equals: true },
        },
        {
            id: 'trust3',
            type: 'text',
            label: 'Line 3 — confirmation call',
            group: 'Reassurance',
            default: '',
            visibleWhen: { field: 'showTrust', equals: true },
        },
        {
            id: 'trust4',
            type: 'text',
            label: 'Line 4 — returns',
            group: 'Reassurance',
            default: '',
            visibleWhen: { field: 'showTrust', equals: true },
        },
        // ── Button ────────────────────────────────────────────────────────────────
        {
            id: 'showCta',
            type: 'checkbox',
            label: 'Show the button',
            group: 'Button',
            default: true,
        },
        {
            id: 'ctaLabel',
            type: 'text',
            label: 'Button label',
            group: 'Button',
            default: '',
            visibleWhen: { field: 'showCta', equals: true },
        },
        {
            id: 'ctaLink',
            type: 'link',
            label: 'Button link',
            group: 'Button',
            info: 'Leave empty to send the buyer back to your home page.',
            visibleWhen: { field: 'showCta', equals: true },
        },
        {
            id: 'showShopName',
            type: 'checkbox',
            label: 'Show the shop name at the bottom',
            group: 'Button',
            default: true,
        },
        // ── Colors ────────────────────────────────────────────────────────────────
        {
            id: 'accentColor',
            type: 'color',
            label: 'Success color',
            group: 'Colors',
            info: 'The tick, the pill, the amount due and the reassurance icons. Empty = the default green.',
            default: { ref: 'custom', value: '' },
        },
        {
            id: 'pageBackground',
            type: 'color',
            label: 'Page background',
            group: 'Colors',
            default: { ref: 'custom', value: '' },
        },
        {
            id: 'cardBackground',
            type: 'color',
            label: 'Card background',
            group: 'Colors',
            default: { ref: 'custom', value: '' },
        },
        {
            id: 'showGlow',
            type: 'checkbox',
            label: 'Show the halo behind the tick',
            group: 'Colors',
            default: true,
        },
        // ── Layout ────────────────────────────────────────────────────────────────
        {
            id: 'cornerRadius',
            type: 'range',
            label: 'Corner radius',
            group: 'Layout',
            min: 0,
            max: 32,
            step: 2,
            unit: 'px',
            default: 20,
        },
        {
            id: 'contentWidth',
            type: 'range',
            label: 'Content width',
            group: 'Layout',
            info: 'How wide the column of cards is allowed to grow on a desktop screen.',
            min: 420,
            max: 820,
            step: 20,
            unit: 'px',
            default: 520,
        },
        {
            id: 'paddingTop',
            type: 'range',
            label: 'Top padding',
            group: 'Section padding',
            min: 0,
            max: 120,
            step: 4,
            unit: 'px',
            default: 44,
        },
        {
            id: 'paddingBottom',
            type: 'range',
            label: 'Bottom padding',
            group: 'Section padding',
            min: 0,
            max: 120,
            step: 4,
            unit: 'px',
            default: 56,
        },
        // The shared "paint the words" control (heading + body, flat colour or gradient), spread last exactly
        // as every other section does — so a seller who learned it on one section finds it here unchanged.
        // This section used to carry a bespoke `textColor` of its own, which SHADOWED the shared field's id;
        // the coverage guard caught it before it reached a single inspector.
        ...textColorFields(),
    ],
    blocks: [],
};
/**
 * The single preset — what "Add section" inserts, and what the default thank-you page is built from. Empty
 * strings everywhere on purpose: see the note on the schema. A preset that carried sentences would write
 * French into an Arabic shop's document the first time it was hydrated.
 */
export const orderConfirmationPresets = [
    {
        name: 'Order confirmation',
        settings: {
            showCheck: true,
            showCodPill: true,
            showSummary: true,
            showThumbnails: true,
            showSavings: true,
            showShipping: true,
            showDelivery: true,
            showCallNote: true,
            showTrust: true,
            showCta: true,
            showShopName: true,
            showGlow: true,
            cornerRadius: 20,
            contentWidth: 520,
            paddingTop: 44,
            paddingBottom: 56,
        },
        blocks: [],
    },
];
/**
 * Not offered in "Add section": the thank-you page already carries exactly one of these, and a page with
 * two order summaries on it would show the buyer their order twice. The group is set anyway so that if the
 * picker ever lists it, it lands somewhere sensible.
 */
export const orderConfirmationMeta = {
    label: 'Order confirmation',
    group: 'Cart',
    icon: 'check-circle',
};
export const orderConfirmationDefinition = {
    type: 'order-confirmation',
    schema: orderConfirmationSchema,
    presets: orderConfirmationPresets,
    meta: orderConfirmationMeta,
};
//# sourceMappingURL=schema.js.map